import { computed, ref, watch } from 'vue'
import { zmgPublicCacheClient } from '@api-client/zmg_cache'
import type { ResType } from '@api-client/typeUtils'
import { isHolidayNoticeActive } from '@lib/zod/dateTimePropEditor'
import { MySettingsStore } from './MySettingsStore'

type HolidayNoticeData = ResType<
    (typeof zmgPublicCacheClient.store.holiday_notice)[':id']['data.json']['$get']
>['data']

export const holidayNoticeByStoreId = ref<Record<string, HolidayNoticeData | null>>({})
/** Tick so active badge re-evaluates when a schedule window ends */
export const holidayNowTick = ref(Date.now())

let tickInterval: ReturnType<typeof setInterval> | null = null
let holidayStatusInitialized = false
let syncInFlight = false
let syncQueued = false

export const loadHolidayNotice = async (storeId: string) => {
    try {
        const { data } = await (
            await zmgPublicCacheClient.store.holiday_notice[':id']['data.json'].$get({
                param: { id: storeId },
            })
        ).json()
        holidayNoticeByStoreId.value = {
            ...holidayNoticeByStoreId.value,
            [storeId]: data ?? null,
        }
    } catch {
        // keep last known value
    }
}

export const isStoreHolidayActiveNow = (storeId: string) => {
    void holidayNowTick.value
    return isHolidayNoticeActive(holidayNoticeByStoreId.value[storeId], holidayNowTick.value)
}

/** Stable string key so watch only fires when the enabled set actually changes */
const enabledStoreIdsKey = computed(() =>
    Object.values(MySettingsStore.value)
        .filter((s) => s.isEnabled)
        .map((s) => s.storeId)
        .sort()
        .join(',')
)

const syncHolidayNoticesForEnabledStores = async () => {
    if (syncInFlight) {
        syncQueued = true
        return
    }
    syncInFlight = true
    try {
        do {
            syncQueued = false
            const ids = enabledStoreIdsKey.value
                ? enabledStoreIdsKey.value.split(',')
                : []
            await Promise.all(ids.map((id) => loadHolidayNotice(id)))
            const next: Record<string, HolidayNoticeData | null> = {}
            for (const id of ids) {
                next[id] = holidayNoticeByStoreId.value[id] ?? null
            }
            holidayNoticeByStoreId.value = next
        } while (syncQueued)
    } finally {
        syncInFlight = false
    }
}

export const initHolidayNoticeStatus = () => {
    if (holidayStatusInitialized) return
    holidayStatusInitialized = true

    holidayNowTick.value = Date.now()
    if (!tickInterval) {
        tickInterval = setInterval(() => {
            holidayNowTick.value = Date.now()
        }, 60_000)
    }

    watch(enabledStoreIdsKey, () => {
        void syncHolidayNoticesForEnabledStores()
    }, { immediate: true })
}
